import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { UserProvider, useUser } from './UserContext';
import SearchView from './SearchView';
import ListView from './ListView';
import Scanner from './Scanner';
import AddProductUser from './AddProductUser';
import Home from './Home';
import logoImage from './logo.jpg';
import Calc from './CalcView';
import Login from './LoginPanel';
import Register from './RegisterPanel';
import AddProduct from './AddProduct';
import ManageUsers from './ManageUsers';
import Settings from './Settings';
import Terms from './Terms';
import Policy from './Policy';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import AboutUs from './AboutUs';
import Contact from './Contact';
import IonIcon from '@reacticons/ionicons';
import LandingPage from './LandingPage';
import InternetStatus from './InternetStatus';
import { ToastContainer } from 'react-toastify';

const CarbohydrateExchangeCalculator = () => {
  const [language, setLanguage] = useState();
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();
  const isiOSDevice = /iPhone|iPod|iPad/.test(navigator.userAgent);

    // Funkcja do odczytywania języka z local storage
    const getStoredLanguage = () => {
      return localStorage.getItem('language');
    };
    
  
  useEffect(() => {


    const storedLanguage = getStoredLanguage();
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    } else {
      const browserLanguage = navigator.language || navigator.userLanguage;
      i18n.changeLanguage(browserLanguage.split('-')[0]);
    }

    const savedMode = localStorage.getItem('themeMode');
    const applyTheme = (mode) => {
      if (mode === 'dark') {
        document.body.classList.add('dark-mode');
      } else if (mode === 'light') {
        document.body.classList.remove('dark-mode');
      } else {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          document.body.classList.add('dark-mode');
        } else {
          document.body.classList.remove('dark-mode');
        }
      }
    };
    applyTheme(savedMode || 'system');
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
  };

  const refresh = () => {
    window.location.reload();
  };

  return (
    <UserProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/policy" element={<Policy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/app/*" element={<MainApp isiOSDevice={isiOSDevice} refresh={refresh} />} />
      </Routes>
    </UserProvider>
  );
};

const MainApp = ({ isiOSDevice, refresh }) => {
  const { logout, user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const checkTokenExpiration = () => {
        const token = user.token;
        if (!token) {
          logout();
          navigate('/app/login'); // Ensure user is logged out if no token is found
          return;
        }
      
        const decodedToken = decodeJwt(token);
        if (!decodedToken || !decodedToken.exp) {
          logout();
          navigate('/app/login'); // Invalid token format, log out user
          return;
        }

        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        if (decodedToken.exp < currentTime) {
          logout();
          navigate('/app/login'); // Token has expired, log out user
        }
      };

      checkTokenExpiration();

      // Optionally, set up interval to periodically check token expiration
      const interval = setInterval(checkTokenExpiration, 60000); // Check every minute

      return () => clearInterval(interval); // Clean up interval on component unmount
    }
  }, [logout, navigate, user]);

  const decodeJwt = (token) => {
    try {
      const payloadBase64 = token.split('.')[1];
      return JSON.parse(atob(payloadBase64));
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };
  const savedMode = localStorage.getItem('themeMode');
  return (
    <div className="app-container">
      <div className="logo">
        {isiOSDevice && (
          <button className="refresh-btn" onClick={refresh}>
            <IonIcon className="refreshicon" name="refresh-outline" />
          </button>
        )}
        <img className="logoimg" src={logoImage} alt="DiabCalc Logo" />
        <p>
          <Link to="/app">DiabCalc</Link>
        </p>
        <Link to="/app/settings" className="settings-btn">
          <IonIcon className="ion" name="settings-outline" size={20} color="white" />
        </Link>
      </div>
      <InternetStatus />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="search" element={<SearchView />} />
        <Route path="listview" element={<ListView />} />
        <Route path="scanner" element={<Scanner />} />
        <Route path="calc" element={<Calc />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="add-product" element={<AddProduct />} />
        <Route path="userslist" element={<ManageUsers />} />
        <Route path="settings" element={<Settings />} />
        <Route path="addproductuser" element={<AddProductUser />} />
      </Routes>
      <NavigationBottom />
      <ToastContainer theme={savedMode} />
    </div>
  );
};

const NavigationBottom = () => {
  const { user } = useUser();
  const { t } = useTranslation();
  const [focusedTab, setFocusedTab] = useState('');
  const location = useLocation();

  const setFocusedTabFromLocation = () => {
    const currentPath = location.pathname.replace('/app', '');
    const routesToTabs = {
      '/': 'Home',
      '/search': 'Search',
      '/calc': 'Calc',
      '/scanner': 'Scanner',
      '/login': 'Login',
      '/register': 'Register',
      '/logout': 'Logout',
      '/meal': 'Meal',
    };

    if (routesToTabs[currentPath] === 'Register') {
      setFocusedTab('Login');
    } else {
      setFocusedTab(routesToTabs[currentPath]);
    }
  };

  useEffect(() => {
    setFocusedTabFromLocation();
  }, [location]);

  const GetIcon = (route) => {
    let iconName;
    if (route === 'Home') {
      iconName = focusedTab === 'Home' ? 'home' : 'home-outline';
    } else if (route === 'Search') {
      iconName = focusedTab === 'Search' ? 'search' : 'search-outline';
    } else if (route === 'Calc') {
      iconName = focusedTab === 'Calc' ? 'calculator' : 'calculator-outline';
    } else if (route === 'Scanner') {
      iconName = focusedTab === 'Scanner' ? 'scan' : 'scan-outline';
    } else if (route === 'Login') {
      iconName = focusedTab === 'Login' ? 'person' : 'person-outline';
    } else if (route === 'Meal') {
      iconName = focusedTab === 'Meal' ? 'restaurant' : 'restaurant-outline';
    }else if (route === 'List') {
      iconName = focusedTab === 'List' ? 'bookmark' : 'bookmark-outline';
    }

    return iconName;
  };

  return (
    <nav className="bottom-nav">
      <NavLink to="/app/" className="nav-link">
        <IonIcon className="ion" name={GetIcon('Home')} size={20} color="blue" />
        {t('home')}
      </NavLink>
      <NavLink to="/app/search" className="nav-link">
        <IonIcon className="ion" name={GetIcon('Search')} size={20} color="blue" />
        {t('search')}
      </NavLink>
      <NavLink to="/app/calc" className="nav-link">
        <IonIcon className="ion" name={GetIcon('Calc')} size={20} color="blue" />
        {t('calc')}
      </NavLink>
      <NavLink to="/app/scanner" className="nav-link scanner">
        <IonIcon className="ion" name={GetIcon('Scanner')} size={20} color="blue" />
        {t('scanner')}
      </NavLink>
      {user ? (
        <NavLink to="/app/listview" className="nav-link">
          <IonIcon className="ion" name={GetIcon('List')} size={20} color="blue" />
          {t('listview')}
        </NavLink>
      ) : (
        <NavLink to="/app/login" className="nav-link">
          <IonIcon className="ion" name={GetIcon('Login')} size={20} color="blue" />
          {t('account')}
        </NavLink>
      )}
    </nav>
  );
};

export default CarbohydrateExchangeCalculator;
